import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CustomerTypeProvider } from "@/contexts/CustomerTypeContext";
const CustomerTypeModal = lazy(() => import("@/components/CustomerTypeModal"));
import { DeferredAuthProvider } from "@/contexts/DeferredAuthContext";
import { CartProvider } from "@/hooks/useCart";
// ProtectedRoute lazy-loaded below to avoid pulling auth deps into entry chunk
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

// Redirect helper: forwards a non-prefixed admin path to the /admin equivalent
// so bookmarks/typed URLs like /analys, /affiliates, /dome-village-intresse
// land on the correct admin page instead of 404.
function AdminPathRedirect() {
  const loc = useLocation();
  return <Navigate to={`/admin${loc.pathname}${loc.search}${loc.hash}`} replace />;
}

// Lazy load non-critical UI components (toasters, banners)
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import("@/components/CookieBanner").then(m => ({ default: m.CookieBanner })));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));

// Defer ChatWidget mount until the browser is idle so its chunk + markdown
// parser don't compete with hero-image LCP.
function DeferredChatWidget() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const schedule = () => {
      if ("requestIdleCallback" in window) {
        const id = requestIdleCallback(() => setReady(true), { timeout: 5000 });
        return () => cancelIdleCallback(id);
      }
      const t = setTimeout(() => setReady(true), 2500);
      return () => clearTimeout(t);
    };
    if (document.readyState === "complete") return schedule();
    let cleanup: (() => void) | undefined;
    const onLoad = () => { cleanup = schedule(); };
    window.addEventListener("load", onLoad);
    return () => {
      window.removeEventListener("load", onLoad);
      cleanup?.();
    };
  }, []);
  if (!ready) return null;
  return <ChatWidget />;
}

// Critical pages - only Index loaded immediately for fastest FCP
import Index from "./pages/Index";

// Lazy loaded pages - loaded on demand for better code splitting
const Catalog = lazy(() => import("./pages/Catalog"));
const ShopIndex = lazy(() => import("./pages/shop/ShopIndex"));
const CategoryPage = lazy(() => import("./pages/shop/CategoryPage"));
const ProductPage = lazy(() => import("./pages/shop/ProductPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const BioceramicDomes = lazy(() => import("./pages/BioceramicDomes"));
const NovaDomes = lazy(() => import("./pages/NovaDomes"));
const PolycarbonateDomes = lazy(() => import("./pages/PolycarbonateDomes"));
const Lampor = lazy(() => import("./pages/Lampor"));
const CartPage = lazy(() => import("./pages/shop/CartPage"));
const CheckoutPage = lazy(() => import("./pages/shop/CheckoutPage"));
const OrderConfirmationPage = lazy(() => import("./pages/shop/OrderConfirmationPage"));
const InstallmentApplicationPage = lazy(() => import("./pages/shop/InstallmentApplicationPage"));
const InstallmentSubmittedPage = lazy(() => import("./pages/shop/InstallmentSubmittedPage"));
const Kopvillkor = lazy(() => import("./pages/Kopvillkor"));
const Angerratt = lazy(() => import("./pages/Angerratt"));
const Integritetspolicy = lazy(() => import("./pages/Integritetspolicy"));
const Cookiepolicy = lazy(() => import("./pages/Cookiepolicy"));
const Leveranspolicy = lazy(() => import("./pages/Leveranspolicy"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const DomeExperiences = lazy(() => import("./pages/DomeExperiences"));
const VisitDome = lazy(() => import("./pages/experiences/VisitDome"));
const StayInDome = lazy(() => import("./pages/experiences/StayInDome"));
const BecomeHost = lazy(() => import("./pages/experiences/BecomeHost"));
const BookingPage = lazy(() => import("./pages/experiences/BookingPage"));
const BookingConfirmation = lazy(() => import("./pages/experiences/BookingConfirmation"));
const DomeVillage = lazy(() => import("./pages/experiences/DomeVillage"));
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const UseCaseHub = lazy(() => import("./pages/use-cases/UseCaseHub"));
const UseCasePage = lazy(() => import("./pages/use-cases/UseCasePage"));
const BygglovKupolGuide = lazy(() => import("./pages/guide/BygglovKupolGuide"));
const Faq = lazy(() => import("./pages/Faq"));

// Affiliate pages - route guards lazy-loaded to keep Supabase SDK out of entry chunk
const AffiliateProtectedRoute = lazy(() => 
  import("@/components/affiliate/AffiliateProtectedRoute")
    .then(m => ({ default: m.AffiliateProtectedRoute }))
);
const AffiliateLogin = lazy(() => import("./pages/affiliate/AffiliateLogin"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/AffiliateDashboard"));

// Admin route guard - lazy-loaded to avoid pulling auth/supabase into entry
const ProtectedRoute = lazy(() => 
  import("@/components/admin/ProtectedRoute")
    .then(m => ({ default: m.ProtectedRoute }))
);

// Admin pages - all lazy loaded
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminOrdersPending = lazy(() => import("./pages/admin/AdminOrdersPending"));
const AdminOrdersShipped = lazy(() => import("./pages/admin/AdminOrdersShipped"));
const AdminOrdersCompleted = lazy(() => import("./pages/admin/AdminOrdersCompleted"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductDetail = lazy(() => import("./pages/admin/AdminProductDetail"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));
const AdminBlogPosts = lazy(() => import("./pages/admin/AdminBlogPosts"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminPages = lazy(() => import("./pages/admin/AdminPages"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminCustomerDetail = lazy(() => import("./pages/admin/AdminCustomerDetail"));
const AdminDomeHosts = lazy(() => import("./pages/admin/AdminDomeHosts"));
const AdminDomeBookings = lazy(() => import("./pages/admin/AdminDomeBookings"));
const AdminPaymentSettings = lazy(() => import("./pages/admin/AdminPaymentSettings"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminDiscountCoupons = lazy(() => import("./pages/admin/AdminDiscountCoupons"));
const AdminDomeVillageInterests = lazy(() => import("./pages/admin/AdminDomeVillageInterests"));
const AdminInstallments = lazy(() => import("./pages/admin/AdminInstallments"));
const AdminSiteSettings = lazy(() => import("./pages/admin/AdminSiteSettings"));

// Loading fallback component - skeleton for better FCP
const PageLoader = () => (
  <div className="min-h-screen bg-background">
    {/* Navigation skeleton */}
    <div className="h-16 bg-muted/30 animate-pulse" />
    {/* Hero skeleton */}
    <div className="h-[60vh] bg-muted/20 animate-pulse" />
    {/* Content skeleton */}
    <div className="container mx-auto px-4 py-12 space-y-6">
      <div className="h-10 w-1/3 bg-muted/30 animate-pulse rounded" />
      <div className="h-5 w-2/3 bg-muted/20 animate-pulse rounded" />
      <div className="h-5 w-1/2 bg-muted/20 animate-pulse rounded" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <div className="h-64 bg-muted/20 animate-pulse rounded-lg" />
        <div className="h-64 bg-muted/20 animate-pulse rounded-lg" />
        <div className="h-64 bg-muted/20 animate-pulse rounded-lg" />
      </div>
    </div>
  </div>
);

// Global affiliate tracking wrapper with prerender signal and SPA redirect handling
function AffiliateTrackingWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  useAffiliateTracking();
  
  // Signal to prerenderer that the app has rendered
  useEffect(() => {
    // Small delay to ensure content is fully rendered
    const timer = setTimeout(() => {
      document.dispatchEvent(new Event('app-rendered'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle SPA redirect from 404.html fallback
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('spa-redirect');
    if (redirectPath && redirectPath !== window.location.pathname) {
      sessionStorage.removeItem('spa-redirect');
      // Use React Router's navigate for reliable cross-browser navigation
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);
  
  return <>{children}</>;
}

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
      <CustomerTypeProvider>
        <DeferredAuthProvider>
          <CartProvider>
            {/* Lazy load toasters - they're not needed for initial render */}
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
            <BrowserRouter>
              <AffiliateTrackingWrapper>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/catalog" element={<Catalog />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/biokeramiska-domes" element={<BioceramicDomes />} />
                  <Route path="/nova-domes" element={<NovaDomes />} />
                  <Route path="/polykarbonat-domes" element={<Navigate to="/panoramakupoler" replace />} />
                  <Route path="/panoramakupoler" element={<PolycarbonateDomes />} />
                  <Route path="/lampor" element={<Lampor />} />
                  {/* Legal pages */}
                  <Route path="/kopvillkor" element={<Kopvillkor />} />
                  <Route path="/angerratt" element={<Angerratt />} />
                  <Route path="/integritetspolicy" element={<Integritetspolicy />} />
                  <Route path="/cookiepolicy" element={<Cookiepolicy />} />
                  <Route path="/leveranspolicy" element={<Leveranspolicy />} />
                  {/* Shop routes */}
                  <Route path="/shop" element={<ShopIndex />} />
                  {/* 301 redirects - gamla polykarbonat-slugar -> nya panoramakupol-slugar */}
                  <Route path="/shop/kategori/polykarbonat-domes" element={<Navigate to="/shop/kategori/panoramakupoler" replace />} />
                  <Route path="/shop/produkt/polykarbonat-dome-10" element={<Navigate to="/shop/produkt/panoramakupol-10" replace />} />
                  <Route path="/shop/produkt/polykarbonat-dome-15" element={<Navigate to="/shop/produkt/panoramakupol-15" replace />} />
                  <Route path="/shop/produkt/polykarbonat-dome-25" element={<Navigate to="/shop/produkt/panoramakupol-25" replace />} />
                  <Route path="/shop/kategori/:slug" element={<CategoryPage />} />
                  <Route path="/shop/produkt/:slug" element={<ProductPage />} />
                  <Route path="/shop/kundvagn" element={<CartPage />} />
                  <Route path="/shop/kassa" element={<CheckoutPage />} />
                  <Route path="/shop/bekraftelse/:orderNumber" element={<OrderConfirmationPage />} />
                  <Route path="/shop/delbetalning/ansok" element={<InstallmentApplicationPage />} />
                  <Route path="/shop/delbetalning/skickad" element={<InstallmentSubmittedPage />} />
                  {/* Blog routes */}
                  <Route path="/blogg" element={<BlogIndex />} />
                  <Route path="/blogg/:slug" element={<BlogPost />} />
                  {/* Experience routes */}
                  <Route path="/upplevelser" element={<DomeExperiences />} />
                  <Route path="/upplevelser/besok" element={<VisitDome />} />
                  <Route path="/upplevelser/overnatta" element={<StayInDome />} />
                  <Route path="/upplevelser/bli-vard" element={<BecomeHost />} />
                  <Route path="/upplevelser/boka/:listingId" element={<BookingPage />} />
                  <Route path="/upplevelser/bokning-bekraftelse" element={<BookingConfirmation />} />
                  <Route path="/upplevelser/dome-village" element={<DomeVillage />} />
                  <Route path="/bli-partner" element={<BecomePartner />} />
                  {/* Use-case landningssidor */}
                  <Route path="/anvandningsomraden" element={<UseCaseHub />} />
                  {/* 301-style SPA redirects — gamla use-case slugs -> nya trädgårdskupol */}
                  <Route path="/anvandningsomraden/kupol-uterum" element={<Navigate to="/anvandningsomraden/tradgardskupol" replace />} />
                  <Route path="/anvandningsomraden/kupol-tradgardsrum" element={<Navigate to="/anvandningsomraden/tradgardskupol" replace />} />
                  <Route path="/anvandningsomraden/:slug" element={<UseCasePage />} />
                  {/* Pillar guide pages */}
                  <Route path="/guide/bygglov-kupol" element={<BygglovKupolGuide />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/vanliga-fragor" element={<Navigate to="/faq" replace />} />
                  {/* Partner routes */}
                  <Route path="/partners/login" element={<AffiliateLogin />} />
                  <Route path="/partners" element={<AffiliateProtectedRoute><AffiliateDashboard /></AffiliateProtectedRoute>} />
                  <Route path="/partners/dashboard" element={<AffiliateProtectedRoute><AffiliateDashboard /></AffiliateProtectedRoute>} />
                  {/* Admin routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="analys" element={<AdminAnalytics />} />
                    <Route path="ordrar" element={<AdminOrders />} />
                    <Route path="ordrar/att-hantera" element={<AdminOrdersPending />} />
                    <Route path="ordrar/skickade" element={<AdminOrdersShipped />} />
                    <Route path="ordrar/fardighanterade" element={<AdminOrdersCompleted />} />
                    <Route path="ordrar/:id" element={<AdminOrderDetail />} />
                    <Route path="produkter" element={<AdminProducts />} />
                    <Route path="produkter/:id" element={<AdminProductDetail />} />
                    <Route path="kategorier" element={<AdminCategories />} />
                    <Route path="overgivna-vagnar" element={<AdminAbandonedCarts />} />
                    <Route path="blogg" element={<AdminBlogPosts />} />
                    <Route path="blogg/:id" element={<AdminBlogEditor />} />
                    <Route path="sidor" element={<AdminPages />} />
                    <Route path="kunder" element={<AdminCustomers />} />
                    <Route path="kunder/:email" element={<AdminCustomerDetail />} />
                    <Route path="dome-vardar" element={<AdminDomeHosts />} />
                    <Route path="dome-bokningar" element={<AdminDomeBookings />} />
                    <Route path="betalningar" element={<AdminPaymentSettings />} />
                    <Route path="affiliates" element={<AdminAffiliates />} />
                    <Route path="rabattkuponger" element={<AdminDiscountCoupons />} />
                    <Route path="dome-village-intresse" element={<AdminDomeVillageInterests />} />
                    <Route path="delbetalningar" element={<AdminInstallments />} />
                    <Route path="installningar" element={<AdminSiteSettings />} />
                  </Route>
                  {/* Top-level redirects for admin paths so bookmarks/typed URLs
                      without the /admin prefix still land on the correct admin page. */}
                  <Route path="/analys" element={<AdminPathRedirect />} />
                  <Route path="/ordrar" element={<AdminPathRedirect />} />
                  <Route path="/ordrar/att-hantera" element={<AdminPathRedirect />} />
                  <Route path="/ordrar/skickade" element={<AdminPathRedirect />} />
                  <Route path="/ordrar/fardighanterade" element={<AdminPathRedirect />} />
                  <Route path="/ordrar/:id" element={<AdminPathRedirect />} />
                  <Route path="/produkter" element={<AdminPathRedirect />} />
                  <Route path="/produkter/:id" element={<AdminPathRedirect />} />
                  <Route path="/kategorier" element={<AdminPathRedirect />} />
                  <Route path="/overgivna-vagnar" element={<AdminPathRedirect />} />
                  <Route path="/sidor" element={<AdminPathRedirect />} />
                  <Route path="/kunder" element={<AdminPathRedirect />} />
                  <Route path="/kunder/:email" element={<AdminPathRedirect />} />
                  <Route path="/dome-vardar" element={<AdminPathRedirect />} />
                  <Route path="/dome-bokningar" element={<AdminPathRedirect />} />
                  <Route path="/betalningar" element={<AdminPathRedirect />} />
                  <Route path="/affiliates" element={<AdminPathRedirect />} />
                  <Route path="/rabattkuponger" element={<AdminPathRedirect />} />
                  <Route path="/dome-village-intresse" element={<AdminPathRedirect />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                {/* Lazy load cookie banner - not critical for initial render */}
                <Suspense fallback={null}>
                  <CookieBanner />
                </Suspense>
                <Suspense fallback={null}>
                  <CustomerTypeModal />
                </Suspense>
                {/* AI Chat widget - global on all public pages */}
                <Suspense fallback={null}>
                  <DeferredChatWidget />
                </Suspense>
              </AffiliateTrackingWrapper>
            </BrowserRouter>
          </CartProvider>
        </DeferredAuthProvider>
      </CustomerTypeProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
